// Reports → Revenue (F-ANL-03, spec/17 R-02): revenue by business day or week — paid at the counter or online, and spent
// from wallets — by category, by how it was paid, discounts, refunds and tax, and how the money that came in becomes
// the revenue (money in − top-ups + top-ups handed back + wallet spends). Every figure is the server's, from the one
// revenue definition; this page only lays them out.

import type { RevenueReport } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { BarChart, ChartEmpty, ChartTable, ChartZeros, ColumnChart, SegmentedControl, StatCard, WaterfallChart } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { PaidByCard, trendSummary, withDelta } from './OverviewTab';
import { KpiFigure, ReportCard, ReportGrid, ReportKpis, ReportNote, ReportPage, ReportRow, ReportRows, Signed } from './report-kit';
import { type ReportScope, useRevenueReport } from './queries';
import { csvMoney, dayLong, dayShort, deltaOf, ExportCsv, moneyShort, moneyTick, NO_DATA, percentOf, shareWords, sparkOf, TabEmpty, TabPending } from './tab-kit';

type Day = RevenueReport['byDay'][number];

/** The Monday of the week `date` falls in (the weeks the chart folds days into). */
export function mondayOf(date: string): string {
  const at = new Date(`${date}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() - ((at.getUTCDay() + 6) % 7));
  return at.toISOString().slice(0, 10);
}

/** Days folded into Monday weeks: each week's revenue, wallet spends and money in summed. */
export function weeksOf(days: readonly Day[], currency: string): Day[] {
  const weeks = new Map<string, Day>();
  for (const day of days) {
    const key = mondayOf(day.date);
    const week = weeks.get(key);
    const add = (a: { amount: number } | null, b: { amount: number } | null) => (a === null && b === null ? null : { amount: (a?.amount ?? 0) + (b?.amount ?? 0), currency });
    weeks.set(
      key,
      week === undefined
        ? { ...day, date: key }
        : {
            date: key,
            revenue: add(week.revenue, day.revenue),
            moneyIn: add(week.moneyIn, day.moneyIn),
            walletSpent: { amount: week.walletSpent.amount + day.walletSpent.amount, currency },
            walletTopUps: { amount: week.walletTopUps.amount + day.walletTopUps.amount, currency },
            bonusSpent: { amount: week.bonusSpent.amount + day.bonusSpent.amount, currency },
          },
    );
  }
  return [...weeks.values()];
}

export function RevenueTab({ scope, previousLabel }: { readonly scope: ReportScope; readonly previousLabel: string }): ReactNode {
  const { t } = useTranslation('console');
  const report = useRevenueReport(scope, true);
  if (report.data === undefined) return <TabPending query={report} unavailable={t('reports.unavailable')} forbidden={t('reports.notPermittedFinancial')} />;
  if (report.data.totals === null && report.data.netSales === null) return <TabEmpty title={t('reports.emptyTitle')} description={t('reports.empty')} />;
  return <Revenue report={report.data} previousLabel={previousLabel} />;
}


function Revenue({ report, previousLabel }: { readonly report: RevenueReport; readonly previousLabel: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const [by, setBy] = useState<'day' | 'week'>('day');
  const short = (amount: number) => moneyShort(format, amount, report.currency);
  const caption = t('reports.vsPrevious', { range: previousLabel });
  const totals = report.totals;
  const periods = by === 'day' ? report.byDay : weeksOf(report.byDay, report.currency);
  const periodLabel = (date: string) => (by === 'day' ? dayLong(format, date) : t('reports.weekOf', { date: dayShort(format, date) }));
  const chartTitle = by === 'day' ? t('reports.revenue.byDay') : t('reports.revenue.byWeek');
  const paidDirect = periods.map((period) => Math.max(0, (period.revenue?.amount ?? 0) - period.walletSpent.amount));
  const fromWallets = periods.map((period) => Math.max(0, period.walletSpent.amount));

  return (
    <ReportPage>
      {report.partial ? <ReportNote>{t('reports.partial')}</ReportNote> : null}
      <ReportKpis label={t('reports.tab.revenue')}>
        <StatCard
          label={t('reports.kpi.revenue')}
          value={<KpiFigure value={totals?.revenue.amount} format={short} />}
          {...withDelta(deltaOf(totals?.revenue.amount, report.previous?.revenue.amount, caption, format))}
          hint={t('reports.revenue.definition')}
          {...sparkOf(report.byDay.map((day) => day.revenue?.amount))}
        />
        <StatCard
          label={t('reports.kpi.netSales')}
          value={<KpiFigure value={report.netSales?.total.amount} format={short} />}
          {...withDelta(deltaOf(report.netSales?.total.amount, report.previousNetSales?.amount, caption, format))}
          hint={t('reports.kpi.netSalesHint')}
        />
        <StatCard
          label={t('reports.kpi.discounts')}
          value={<KpiFigure value={report.netSales?.discounts.amount} format={short} />}
          hint={t('reports.kpi.discountsHint')}
        />
        <StatCard
          label={t('reports.kpi.refunds')}
          value={<KpiFigure value={totals?.refunds.amount} format={short} />}
          hint={t('reports.kpi.refundRate', { rate: percentOf(format, report.refundRate?.basisPoints, 1) })}
        />
      </ReportKpis>

      <ReportCard
        title={chartTitle}
        description={t('reports.businessDays')}
        action={
          <SegmentedControl<'day' | 'week'>
            label={t('reports.revenue.groupBy')}
            value={by}
            onValueChange={setBy}
            options={[
              { value: 'day', label: t('reports.revenue.days') },
              { value: 'week', label: t('reports.revenue.weeks') },
            ]}
          />
        }
      >
        <ColumnChart
          label={chartTitle}
          summary={trendSummary(t, periods.map((period) => period.revenue?.amount ?? null), (index) => periodLabel(periods[index]?.date ?? ''), short)}
          categories={periods.map((period) => dayShort(format, period.date))}
          categoryLabel={(index) => periodLabel(periods[index]?.date ?? '')}
          series={[
            { key: 'paid', label: t('reports.revenue.paidDirect'), tone: 1, values: paidDirect },
            { key: 'wallet', label: t('reports.revenue.fromWallets'), tone: 5, values: fromWallets },
          ]}
          format={short}
          formatTick={(value) => moneyTick(format, value, report.currency)}
          height={240}
        />
        <ChartTable
          caption={chartTitle}
          rows={periods}
          rowKey={(row) => row.date}
          columns={[
            { key: 'date', header: by === 'day' ? t('reports.column.day') : t('reports.column.week'), cell: (row) => periodLabel(row.date) },
            { key: 'revenue', header: t('reports.kpi.revenue'), numeric: true, cell: (row) => <span className="console-money">{row.revenue === null ? NO_DATA : format.money(row.revenue)}</span> },
            { key: 'wallet', header: t('reports.revenue.fromWallets'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.walletSpent)}</span> },
            { key: 'moneyIn', header: t('reports.kpi.moneyIn'), numeric: true, cell: (row) => <span className="console-money">{row.moneyIn === null ? NO_DATA : format.money(row.moneyIn)}</span> },
            { key: 'topUps', header: t('reports.column.topUps'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.walletTopUps)}</span> },
          ]}
        />
        <div className="console-rt-actions">
          <ExportCsv
            fileName={`revenue-${report.from}-${report.to}.csv`}
            header={['date', 'revenue', 'wallet_spent', 'money_in', 'wallet_top_ups', 'bonus_spent', 'currency']}
            rows={report.byDay.map((day) => [day.date, csvMoney(day.revenue), csvMoney(day.walletSpent), csvMoney(day.moneyIn), csvMoney(day.walletTopUps), csvMoney(day.bonusSpent), report.currency])}
          />
        </div>
      </ReportCard>

      <ReportGrid layout="even">
        <CategoriesCard report={report} />
        <PaidByCard revenue={report} />
      </ReportGrid>

      {totals !== null ? (
        <ReportCard title={t('reports.revenue.fromMoneyIn')} description={t('reports.revenue.fromMoneyInHint')}>
          <WaterfallChart
            label={t('reports.revenue.fromMoneyIn')}
            steps={[
              { key: 'moneyIn', label: t('reports.kpi.moneyIn'), hint: t('reports.revenue.moneyInHint'), value: totals.moneyIn.amount, total: true, display: format.money(totals.moneyIn) },
              { key: 'topUps', label: t('reports.column.topUps'), hint: t('reports.revenue.topUpsHint'), value: -totals.walletTopUps.amount, display: <Signed sign="−" amount={format.money(totals.walletTopUps)} /> },
              { key: 'handedBack', label: t('reports.revenue.handedBack'), value: totals.walletHandedBack.amount, display: <Signed sign="+" amount={format.money(totals.walletHandedBack)} /> },
              { key: 'wallet', label: t('reports.revenue.fromWallets'), hint: t('reports.revenue.fromWalletsHint'), value: totals.walletSpent.amount, display: <Signed sign="+" amount={format.money(totals.walletSpent)} /> },
              { key: 'revenue', label: t('reports.kpi.revenue'), value: totals.revenue.amount, total: true, display: format.money(totals.revenue) },
            ].filter((step) => step.total === true || step.value !== 0)}
          />
          {totals.bonusSpent.amount > 0 ? <ReportNote>{t('reports.revenue.bonusNote', { amount: format.money(totals.bonusSpent) })}</ReportNote> : null}
        </ReportCard>
      ) : null}
    </ReportPage>
  );
}

/** Sales by category as ranked bars (never a negative bar: wallet credit is its own line), then how they net down. */
function CategoriesCard({ report }: { readonly report: RevenueReport }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const rows = report.netSales?.byCategory ?? [];
  const sold = rows.filter((row) => row.amount.amount > 0).sort((a, b) => b.amount.amount - a.amount.amount);
  const gross = sold.reduce((sum, row) => sum + row.amount.amount, 0);
  const nothing = rows.filter((row) => row.amount.amount === 0);
  const below = rows.filter((row) => row.amount.amount < 0);
  const netSales = report.netSales;
  return (
    <ReportCard title={t('reports.revenue.byCategory')} description={t('reports.revenue.byCategoryHint')}>
      {sold.length === 0 ? (
        <ChartEmpty>{t('reports.noData')}</ChartEmpty>
      ) : (
        <BarChart
          label={t('reports.revenue.byCategory')}
          rows={sold.map((row) => ({
            key: row.category,
            label: t(`reports.category.${row.category}`),
            value: row.amount.amount,
            display: format.money(row.amount),
            meta: shareWords(format, gross === 0 ? 0 : row.amount.amount / gross),
          }))}
        />
      )}
      <ChartZeros label={t('reports.revenue.notSold')} items={nothing.map((row) => ({ key: row.category, label: t(`reports.category.${row.category}`) }))} />
      {below.length > 0 ? (
        <ReportNote>{t('reports.revenue.refundedMore', { categories: below.map((row) => `${t(`reports.category.${row.category}`)} ${format.money(row.amount)}`).join(', ') })}</ReportNote>
      ) : null}
      <ReportRows>
        <ReportRow label={t('reports.kpi.discounts')} value={netSales === null ? NO_DATA : `− ${format.money(netSales.discounts)}`} quiet={netSales?.discounts.amount === 0} />
        <ReportRow
          label={t('reports.revenue.walletCredit')}
          hint={t('reports.revenue.walletCreditHint')}
          value={netSales === null ? NO_DATA : `− ${format.money(netSales.walletCredit)}`}
          quiet={netSales?.walletCredit.amount === 0}
        />
        <ReportRow strong label={t('reports.kpi.netSales')} value={netSales === null ? NO_DATA : format.money(netSales.total)} />
        <ReportRow label={t('reports.revenue.tax')} value={report.tax === null ? NO_DATA : format.money(report.tax.total)} quiet={report.tax === null} />
        {report.tax?.paidAtTopUp !== undefined && report.tax.paidAtTopUp.amount !== 0 ? (
          <ReportRow label={t('reports.revenue.taxPaidAtTopUp')} hint={t('reports.revenue.taxPaidAtTopUpHint')} value={format.money(report.tax.paidAtTopUp)} />
        ) : null}
      </ReportRows>
    </ReportCard>
  );
}
